<script setup lang="ts">
import { computed, h, type PropType } from 'vue';
import GaiaButton from './GaiaButton.vue';
import GaiaIconButton from './GaiaIconButton.vue';
import GaiaTooltip from './GaiaTooltip.vue';
import { gaiaDangerIconButtonOverrides, gaiaTextButtonOverrides } from '../themes/tokens';

// Listeners and attrs belong on the button, not on the tooltip wrapping it.
defineOptions({ inheritAttrs: false });

const props = defineProps({
  /**
   * `delete` destroys a record for good and is always behind a confirm — it
   * gets the trash can. `remove` takes an item out of a list the user is still
   * assembling (a filter condition, a recipient, an unsaved option) and is
   * undone by adding it back — it gets the ✕. Picking the glyph here is the
   * point of the component: the two were drawn interchangeably before.
   */
  intent: {
    type: String as PropType<'delete' | 'remove'>,
    default: 'delete',
  },
  /** Glyph-only affordance, or the danger text button that spells the verb. */
  variant: {
    type: String as PropType<'icon' | 'label'>,
    default: 'icon',
  },
  /**
   * Square edge of the icon variant: `xs` for dense rows (palette items,
   * filter conditions), `sm` everywhere else, `md` for data-table row actions.
   */
  size: {
    type: String as PropType<'xs' | 'sm' | 'md'>,
    default: 'sm',
  },
  /**
   * What is being acted on — a noun ("review", "column") or the record's own
   * name. Composes the verb into "Delete review", which becomes the visible
   * label and the affordance's title/aria-label in one go.
   */
  subject: {
    type: String,
    default: '',
  },
  /** Replaces the composed title — for spelling out why the action is off. */
  title: {
    type: String,
    default: '',
  },
  /** Icon variant: name the action in a GaiaTooltip rather than the native title. */
  tooltip: {
    type: Boolean,
    default: false,
  },
});

const DIMENSION = { xs: 22, sm: 26, md: 28 } as const;

const dimension = computed(() => DIMENSION[props.size]);

// The glyph reads right at half the square's edge — 11/13/14px, the sizes the
// hand-rolled SVGs had landed on anyway.
const glyphSize = computed(() => dimension.value / 2);

const text = computed(() => {
  const verb = props.intent === 'delete' ? 'Delete' : 'Remove';
  return props.subject ? `${verb} ${props.subject}` : verb;
});

// A 16×16 stroke pair, matching the redesign's icon language rather than any
// of the four icon packs the app pulls in.
const path = computed(() =>
  props.intent === 'delete'
    ? 'M3.2 4.6h9.6M6.4 4.6V3.2h3.2v1.4M4.6 4.6l.6 8.2h5.6l.6-8.2'
    : 'M4 4l8 8M12 4l-8 8'
);

// One glyph for both icon branches.
const Glyph = (): ReturnType<typeof h> =>
  h(
    'svg',
    {
      width: glyphSize.value,
      height: glyphSize.value,
      viewBox: '0 0 16 16',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.5,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    },
    [h('path', { d: path.value })]
  );
</script>

<template>
  <!-- The one delete/remove affordance. Call sites choose intent and variant;
       the glyph, the danger hover wash, the square size and the accessible
       name all come from here, so they can't drift apart again. The confirm
       button inside a destructive dialog is NOT this component — that is a
       dialog's commit action, a plain GaiaButton with type="error". -->
  <gaia-button
    v-if="props.variant === 'label'"
    v-bind="$attrs"
    text
    :size="props.size === 'xs' ? 'tiny' : 'small'"
    :theme-overrides="gaiaTextButtonOverrides.danger"
    :title="props.title || undefined"
  >
    {{ text }}
  </gaia-button>
  <gaia-tooltip v-else-if="props.tooltip">
    <template #trigger>
      <gaia-icon-button
        v-bind="$attrs"
        :dimension="dimension"
        size="tiny"
        :theme-overrides="gaiaDangerIconButtonOverrides"
        :aria-label="text"
      >
        <glyph />
      </gaia-icon-button>
    </template>
    {{ props.title || text }}
  </gaia-tooltip>
  <gaia-icon-button
    v-else
    v-bind="$attrs"
    :dimension="dimension"
    size="tiny"
    :theme-overrides="gaiaDangerIconButtonOverrides"
    :title="props.title || text"
    :aria-label="text"
  >
    <glyph />
  </gaia-icon-button>
</template>
